import React, { useState, useRef } from 'react';
import * as XLSX from 'xlsx';
import '../Dropdown/Dropdown.css';
import { getAuthToken } from '../utils/auth';
import BASE_URL from '../utils/api';

// ─── Colunas do template (na ordem) ──────────────────────────────────────────
const COLUNAS_TEMPLATE = [
  // Básicos
  'Nome',
  'Descricao',
  'Preco',
  'EstoqueFisico',
  'EstoqueMinimo',
  'Unidade',
  'CodigoBarras',
  'Categoria',
  'Ativo',
  // Fiscal
  'NCM',
  'CFOP',
  'CEST',
  'OrigemMercadoria',
  'AliquotaICMS',
  'CST_ICMS',
  'CSOSN',
  'AliquotaIPI',
  'CST_IPI',
  'AliquotaPIS',
  'CST_PIS',
  'AliquotaCOFINS',
  'CST_COFINS',
  // Dimensões e peso
  'PesoLiquido',
  'PesoBruto',
  'Altura',
  'Largura',
  'Profundidade'
];

const CAMPOS_OBRIGATORIOS = ['Nome', 'Preco'];

// ─── API helper ───────────────────────────────────────────────────────────────
const apiCall = async (endpoint, method = 'GET', data = null) => {
  const urls = [
    `${BASE_URL}/${endpoint}`,
    `${BASE_URL}/${endpoint}`,
    `${BASE_URL}/${endpoint}`,
    `${BASE_URL}/${endpoint}`,
    `${BASE_URL}/${endpoint}`
  ];
  for (const url of urls) {
    try {
      const options = {
        method,
        headers: { 'Content-Type': 'application/json' }
      };
      const token = getAuthToken();
      if (token) options.headers['Authorization'] = `Bearer ${token}`;
      if (data && method !== 'GET') options.body = JSON.stringify(data);
      const response = await fetch(url, options);
      if (response.ok) return method === 'GET' ? await response.json() : response;
    } catch (_) {}
  }
  throw new Error(`Nenhuma URL da API funcionou para ${method} ${endpoint}.`);
};

export default function PopupImportacaoProdutosEmMassa({ onFechar, onSucesso }) {
  const [arquivo, setArquivo] = useState(null);
  const [processando, setProcessando] = useState(false);
  const [progresso, setProgresso] = useState({ atual: 0, total: 0 });
  const [resultados, setResultados] = useState([]);
  const [erroArquivo, setErroArquivo] = useState('');
  const [concluido, setConcluido] = useState(false);
  const inputRef = useRef(null);
  const abortadoRef = useRef(false);

  // ─── Gerar e baixar template XLSX ────────────────────────────────────────────
  const baixarTemplate = () => {
    const EXEMPLOS = [
      {
        Nome: 'Notebook Dell Inspiron',
        Descricao: 'Notebook 15 pol, i5, 8GB RAM, 256GB SSD',
        Preco: 2500.00,
        EstoqueFisico: 10,
        EstoqueMinimo: 3,
        Unidade: 'UN',
        CodigoBarras: '7891234567890',
        Categoria: 'Informática',
        Ativo: 'true',
        NCM: '84713012',
        CFOP: '5102',
        CEST: '',
        OrigemMercadoria: 0,
        AliquotaICMS: 12,
        CST_ICMS: '00',
        CSOSN: '102',
        AliquotaIPI: 0,
        CST_IPI: '99',
        AliquotaPIS: 0.65,
        CST_PIS: '01',
        AliquotaCOFINS: 3,
        CST_COFINS: '01',
        PesoLiquido: 1.8,
        PesoBruto: 2.2,
        Altura: '',
        Largura: '',
        Profundidade: ''
      },
      {
        Nome: 'Monitor LG 24"',
        Descricao: 'Monitor Full HD 24 polegadas',
        Preco: 899.90,
        EstoqueFisico: 5,
        EstoqueMinimo: 2,
        Unidade: 'UN',
        CodigoBarras: '7890987654321',
        Categoria: 'Informática',
        Ativo: 'true',
        NCM: '85285290',
        CFOP: '5102',
        CEST: '',
        OrigemMercadoria: 0,
        AliquotaICMS: 12,
        CST_ICMS: '00',
        CSOSN: '102',
        AliquotaIPI: 0,
        CST_IPI: '99',
        AliquotaPIS: 0.65,
        CST_PIS: '01',
        AliquotaCOFINS: 3,
        CST_COFINS: '01',
        PesoLiquido: 3.5,
        PesoBruto: 4.0,
        Altura: 40,
        Largura: 55,
        Profundidade: 20
      }
    ];

    const ws = XLSX.utils.json_to_sheet(EXEMPLOS, { header: COLUNAS_TEMPLATE });
    ws['!cols'] = COLUNAS_TEMPLATE.map(col => ({ wch: Math.max(col.length + 4, 16) }));

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Produtos');

    // Segunda aba com instruções
    const instrucoes = [
      { Campo: 'Nome',              Obrigatorio: 'SIM', Tipo: 'Texto',   Descricao: 'Nome do produto' },
      { Campo: 'Descricao',         Obrigatorio: 'Não', Tipo: 'Texto',   Descricao: 'Descrição detalhada do produto' },
      { Campo: 'Preco',             Obrigatorio: 'SIM', Tipo: 'Decimal', Descricao: 'Preço de venda (ex: 2500.00)' },
      { Campo: 'EstoqueFisico',     Obrigatorio: 'Não', Tipo: 'Inteiro', Descricao: 'Quantidade em estoque físico' },
      { Campo: 'EstoqueMinimo',     Obrigatorio: 'Não', Tipo: 'Inteiro', Descricao: 'Estoque mínimo para alerta' },
      { Campo: 'Unidade',           Obrigatorio: 'Não', Tipo: 'Texto',   Descricao: 'Unidade: UN, KG, M, L, CX, etc. (padrão: UN)' },
      { Campo: 'CodigoBarras',      Obrigatorio: 'Não', Tipo: 'Texto',   Descricao: 'EAN / código de barras do produto' },
      { Campo: 'Categoria',         Obrigatorio: 'Não', Tipo: 'Texto',   Descricao: 'Nome da categoria (ex: Informática)' },
      { Campo: 'Ativo',             Obrigatorio: 'Não', Tipo: 'Booleano',Descricao: 'true = ativo, false = inativo (padrão: true)' },
      { Campo: 'NCM',               Obrigatorio: 'Não', Tipo: 'Texto',   Descricao: 'Código NCM fiscal (8 dígitos)' },
      { Campo: 'CFOP',              Obrigatorio: 'Não', Tipo: 'Texto',   Descricao: 'CFOP (padrão: 5102)' },
      { Campo: 'CEST',              Obrigatorio: 'Não', Tipo: 'Texto',   Descricao: 'Código CEST (substituição tributária)' },
      { Campo: 'OrigemMercadoria',  Obrigatorio: 'Não', Tipo: 'Inteiro', Descricao: '0=Nacional, 1=Estrangeira importada, etc.' },
      { Campo: 'AliquotaICMS',      Obrigatorio: 'Não', Tipo: 'Decimal', Descricao: 'Alíquota ICMS (%)' },
      { Campo: 'CST_ICMS',          Obrigatorio: 'Não', Tipo: 'Texto',   Descricao: 'CST do ICMS (ex: 00, 10, 20)' },
      { Campo: 'CSOSN',             Obrigatorio: 'Não', Tipo: 'Texto',   Descricao: 'CSOSN para Simples Nacional (ex: 102)' },
      { Campo: 'AliquotaIPI',       Obrigatorio: 'Não', Tipo: 'Decimal', Descricao: 'Alíquota IPI (%)' },
      { Campo: 'CST_IPI',           Obrigatorio: 'Não', Tipo: 'Texto',   Descricao: 'CST do IPI (ex: 99)' },
      { Campo: 'AliquotaPIS',       Obrigatorio: 'Não', Tipo: 'Decimal', Descricao: 'Alíquota PIS (%)' },
      { Campo: 'CST_PIS',           Obrigatorio: 'Não', Tipo: 'Texto',   Descricao: 'CST do PIS (ex: 01, 99)' },
      { Campo: 'AliquotaCOFINS',    Obrigatorio: 'Não', Tipo: 'Decimal', Descricao: 'Alíquota COFINS (%)' },
      { Campo: 'CST_COFINS',        Obrigatorio: 'Não', Tipo: 'Texto',   Descricao: 'CST do COFINS (ex: 01, 99)' },
      { Campo: 'PesoLiquido',       Obrigatorio: 'Não', Tipo: 'Decimal', Descricao: 'Peso líquido em kg' },
      { Campo: 'PesoBruto',         Obrigatorio: 'Não', Tipo: 'Decimal', Descricao: 'Peso bruto em kg' },
      { Campo: 'Altura',            Obrigatorio: 'Não', Tipo: 'Decimal', Descricao: 'Altura em cm' },
      { Campo: 'Largura',           Obrigatorio: 'Não', Tipo: 'Decimal', Descricao: 'Largura em cm' },
      { Campo: 'Profundidade',      Obrigatorio: 'Não', Tipo: 'Decimal', Descricao: 'Profundidade/comprimento em cm' },
    ];
    const wsInst = XLSX.utils.json_to_sheet(instrucoes, { header: ['Campo', 'Obrigatorio', 'Tipo', 'Descricao'] });
    wsInst['!cols'] = [{ wch: 20 }, { wch: 12 }, { wch: 12 }, { wch: 60 }];
    XLSX.utils.book_append_sheet(wb, wsInst, 'Instruções');

    XLSX.writeFile(wb, 'template_importacao_produtos.xlsx');
  };

  // ─── Validar cabeçalho ────────────────────────────────────────────────────────
  const validarColunas = (colunas) => {
    const faltando = COLUNAS_TEMPLATE.filter(c => !colunas.includes(c));
    return { faltando };
  };

  // ─── Parseadores seguros ──────────────────────────────────────────────────────
  const parseDecimal = (v, def = 0) => {
    if (v === '' || v === undefined || v === null) return def;
    const n = parseFloat(String(v).replace(',', '.'));
    return isNaN(n) ? def : n;
  };

  const parseInteiro = (v, def = 0) => {
    if (v === '' || v === undefined || v === null) return def;
    const n = parseInt(String(v), 10);
    return isNaN(n) ? def : n;
  };

  const parseAtivo = (v) => {
    if (v === '' || v === undefined || v === null) return true;
    const s = String(v).toLowerCase().trim();
    return s !== 'false' && s !== '0' && s !== 'não' && s !== 'nao' && s !== 'n';
  };

  // ─── Upload ───────────────────────────────────────────────────────────────────
  const handleArquivo = (e) => {
    const f = e.target.files && e.target.files[0];
    setErroArquivo('');
    setResultados([]);
    setConcluido(false);
    if (!f) return;
    const ext = f.name.split('.').pop().toLowerCase();
    if (!['xlsx', 'xls'].includes(ext)) {
      setErroArquivo('Formato inválido. Utilize somente arquivos .xlsx ou .xls do template padrão do sistema.');
      setArquivo(null);
      return;
    }
    setArquivo(f);
  };

  const iniciarImportacao = async () => {
    if (!arquivo) return;
    setErroArquivo('');
    setResultados([]);
    setConcluido(false);
    abortadoRef.current = false;

    // Ler arquivo
    let linhas;
    try {
      const buffer = await arquivo.arrayBuffer();
      const wb = XLSX.read(buffer, { type: 'array' });
      const ws = wb.Sheets[wb.SheetNames[0]];
      linhas = XLSX.utils.sheet_to_json(ws, { defval: '' });
    } catch {
      setErroArquivo('Não foi possível ler o arquivo. Verifique se é um arquivo Excel válido.');
      return;
    }

    if (!linhas || linhas.length === 0) {
      setErroArquivo('O arquivo não contém dados. Verifique se preencheu as linhas na planilha.');
      return;
    }

    // Validar colunas
    const colunasArquivo = Object.keys(linhas[0]);
    const { faltando } = validarColunas(colunasArquivo);
    if (faltando.length > 0) {
      setErroArquivo(
        `As colunas abaixo estão faltando no arquivo. Utilize apenas o template padrão do sistema para importação:\n• ${faltando.join('\n• ')}`
      );
      return;
    }

    // Processar linha a linha
    setProcessando(true);
    setProgresso({ atual: 0, total: linhas.length });
    const logs = [];

    for (let i = 0; i < linhas.length; i++) {
      if (abortadoRef.current) break;
      const linha = linhas[i];
      const numLinha = i + 1;
      setProgresso({ atual: i + 1, total: linhas.length });

      // Validar campos obrigatórios
      const errosObrig = CAMPOS_OBRIGATORIOS.filter(c => !String(linha[c] || '').trim());
      if (errosObrig.length > 0) {
        logs.push({
          linha: numLinha,
          nome: linha.Nome || `Linha ${numLinha}`,
          status: 'erro',
          mensagem: `Campos obrigatórios ausentes: ${errosObrig.join(', ')}`
        });
        setResultados([...logs]);
        continue;
      }

      // Montar payload alinhado com FormularioProduto / ProdutoController
      const codigoBarras = String(linha.CodigoBarras || '').trim();
      const payload = {
        nome:                    String(linha.Nome).trim(),
        descricao:               String(linha.Descricao || '').trim(),
        preco:                   parseDecimal(linha.Preco),
        estoqueFisico:           parseInteiro(linha.EstoqueFisico),
        estoqueReservado:        0,
        estoqueMinimo:           parseInteiro(linha.EstoqueMinimo),
        unidade:                 String(linha.Unidade || 'UN').trim() || 'UN',
        codigoBarras:            codigoBarras,
        categoria:               String(linha.Categoria || '').trim(),
        ativo:                   parseAtivo(linha.Ativo),
        // Fiscal
        ncm:                     String(linha.NCM || '').trim(),
        gtin:                    codigoBarras, // GTIN = código de barras (EAN)
        cfop:                    String(linha.CFOP || '5102').trim() || '5102',
        cest:                    String(linha.CEST || '').trim(),
        unidadeTributaria:       String(linha.Unidade || 'UN').trim() || 'UN',
        origemMercadoria:        parseInteiro(linha.OrigemMercadoria),
        aliquotaICMS:            parseDecimal(linha.AliquotaICMS),
        csT_ICMS:                String(linha.CST_ICMS || '00').trim(),
        csosn:                   String(linha.CSOSN || '102').trim(),
        modalidadeBCICMS:        3,
        percentualReducaoBC:     0,
        aliquotaICMS_ST:         0,
        mvA_ST:                  0,
        aliquotaIPI:             parseDecimal(linha.AliquotaIPI),
        csT_IPI:                 String(linha.CST_IPI || '99').trim(),
        codigoEnquadramentoIPI:  '999',
        aliquotaPIS:             parseDecimal(linha.AliquotaPIS),
        csT_PIS:                 String(linha.CST_PIS || '99').trim(),
        aliquotaCOFINS:          parseDecimal(linha.AliquotaCOFINS),
        csT_COFINS:              String(linha.CST_COFINS || '99').trim(),
        aliquotaFCP:             0,
        // Peso e Dimensões
        pesoLiquido:             parseDecimal(linha.PesoLiquido),
        pesoBruto:               parseDecimal(linha.PesoBruto),
        altura:                  parseDecimal(linha.Altura),
        largura:                 parseDecimal(linha.Largura),
        profundidade:            parseDecimal(linha.Profundidade),
        // Defaults
        beneficioFiscal:         '',
        informacoesAdicionaisProduto: '',
        dataCadastro:            new Date().toISOString().split('T')[0],
        regimeTributario:        'simples'
      };

      try {
        const resp = await apiCall('Produto', 'POST', payload);
        if (resp && (resp.ok || resp.status === 200 || resp.status === 201)) {
          logs.push({ linha: numLinha, nome: payload.nome, status: 'sucesso', mensagem: 'Produto cadastrado com sucesso' });
        } else {
          const txt = typeof resp.text === 'function' ? await resp.text() : 'Erro desconhecido';
          logs.push({ linha: numLinha, nome: payload.nome, status: 'erro', mensagem: txt || 'Erro ao cadastrar' });
        }
      } catch (err) {
        logs.push({ linha: numLinha, nome: payload.nome, status: 'erro', mensagem: err.message });
      }

      setResultados([...logs]);
      await new Promise(r => setTimeout(r, 80));
    }

    setProcessando(false);
    setConcluido(true);
    if (onSucesso) onSucesso();
  };

  const cancelar = () => { abortadoRef.current = true; };

  const sucessos = resultados.filter(r => r.status === 'sucesso').length;
  const erros   = resultados.filter(r => r.status === 'erro').length;
  const pct     = progresso.total > 0 ? Math.round((progresso.atual / progresso.total) * 100) : 0;

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.52)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 5000
      }}
      onClick={processando ? undefined : onFechar}
    >
      <div
        style={{
          background: '#fff', borderRadius: 10, width: 720, maxWidth: '96vw',
          maxHeight: '92vh', overflowY: 'auto',
          boxShadow: '0 8px 40px rgba(0,0,0,0.28)', position: 'relative'
        }}
        onClick={e => e.stopPropagation()}
      >

        {/* ── Cabeçalho ──────────────────────────────────────────────────────── */}
        <div style={{
          background: 'linear-gradient(135deg, rgb(235, 133, 0), rgb(208, 116, 0))',
          padding: '18px 24px', borderRadius: '10px 10px 0 0',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between'
        }}>
          <div>
            <h2 style={{ color: '#fff', margin: 0, fontSize: 20, fontWeight: 700 }}>
              📦 Cadastro em Massa de Produtos
            </h2>
            <p style={{ color: 'rgba(255,255,255,0.85)', margin: '4px 0 0', fontSize: 13 }}>
              Importe vários produtos de uma vez usando a planilha Excel padrão do sistema
            </p>
          </div>
          {!processando && (
            <button
              onClick={onFechar}
              style={{
                background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff',
                borderRadius: '50%', width: 32, height: 32, fontSize: 20,
                cursor: 'pointer', fontWeight: 700, lineHeight: 1
              }}
            >×</button>
          )}
        </div>

        <div style={{ padding: '22px 26px' }}>

          {/* ── Instruções ─────────────────────────────────────────────────────── */}
          <div style={{
            background: '#fff8e1', border: '1.5px solid #ffe082',
            borderRadius: 8, padding: '14px 18px', marginBottom: 22
          }}>
            <p style={{ fontWeight: 700, color: 'rgb(184, 98, 0)', marginBottom: 8, fontSize: 14 }}>
              📋 Como funciona o Cadastro em Massa de Produtos?
            </p>
            <ol style={{ margin: 0, paddingLeft: 20, color: '#5d4037', fontSize: 13, lineHeight: 1.7 }}>
              <li>Clique em <strong>"Baixar Template Excel"</strong> para obter a planilha padrão com todos os campos do formulário de produto.</li>
              <li>Preencha os dados — <strong>uma linha por produto</strong>. Os campos <strong>Nome</strong> e <strong>Preço</strong> são obrigatórios.</li>
              <li>Campos fiscais (NCM, CFOP, alíquotas) e dimensões são opcionais — deixe em branco se não se aplicar.</li>
              <li>Salve o arquivo no formato <strong>.xlsx</strong> e faça o upload abaixo.</li>
              <li><strong>Atenção:</strong> somente o template padrão do sistema é aceito. Arquivos com colunas diferentes serão rejeitados.</li>
            </ol>
          </div>

          {/* ── Download template ───────────────────────────────────────────────── */}
          <div style={{ textAlign: 'center', marginBottom: 22 }}>
            <button
              onClick={baixarTemplate}
              disabled={processando}
              style={{
                background: 'linear-gradient(135deg, #43a047, #2e7d32)',
                color: '#fff', border: 'none', borderRadius: 8,
                padding: '11px 28px', fontSize: 14, fontWeight: 700,
                cursor: processando ? 'not-allowed' : 'pointer',
                boxShadow: '0 3px 10px rgba(67,160,71,0.3)',
                display: 'inline-flex', alignItems: 'center', gap: 8
              }}
            >
              ⬇️ Baixar Template Excel
            </button>
            <p style={{ color: '#888', fontSize: 12, marginTop: 6 }}>
              Contém todos os campos do formulário de produto com exemplos de preenchimento e aba de instruções
            </p>
          </div>

          <hr style={{ border: 'none', borderTop: '1px solid #e0e0e0', marginBottom: 20 }} />

          {/* ── Upload ─────────────────────────────────────────────────────────── */}
          {!processando && !concluido && (
            <div>
              <label style={{ fontWeight: 700, color: '#333', fontSize: 14, display: 'block', marginBottom: 8 }}>
                Selecionar Planilha Excel (.xlsx / .xls)
              </label>
              <div
                style={{
                  border: '2px dashed rgb(235, 133, 0)', borderRadius: 8,
                  padding: '28px 20px', textAlign: 'center',
                  background: arquivo ? '#fff8e1' : '#fafafa',
                  cursor: 'pointer', transition: 'all .2s'
                }}
                onClick={() => inputRef.current && inputRef.current.click()}
              >
                <div style={{ fontSize: 36, marginBottom: 8 }}>📂</div>
                <p style={{ color: '#616161', margin: 0, fontSize: 14 }}>
                  {arquivo
                    ? <><span style={{ color: '#388e3c' }}>✅</span> <strong>{arquivo.name}</strong></>
                    : 'Clique aqui ou arraste a planilha preenchida'}
                </p>
                <p style={{ color: '#aaa', margin: '4px 0 0', fontSize: 12 }}>
                  Somente arquivos .xlsx ou .xls gerados pelo template padrão
                </p>
              </div>
              <input
                ref={inputRef}
                type="file"
                accept=".xlsx,.xls"
                style={{ display: 'none' }}
                onChange={handleArquivo}
              />

              {erroArquivo && (
                <div style={{
                  background: '#ffebee', border: '1.5px solid #ef9a9a',
                  borderRadius: 8, padding: '12px 16px', marginTop: 14,
                  color: '#c62828', fontSize: 13, whiteSpace: 'pre-line'
                }}>
                  ❌ {erroArquivo}
                </div>
              )}

              {arquivo && !erroArquivo && (
                <div style={{ textAlign: 'center', marginTop: 18 }}>
                  <button
                    onClick={iniciarImportacao}
                    style={{
                      background: 'linear-gradient(135deg, rgb(235, 133, 0), rgb(208, 116, 0))',
                      color: '#fff', border: 'none', borderRadius: 8,
                      padding: '12px 36px', fontSize: 15, fontWeight: 700,
                      cursor: 'pointer', boxShadow: '0 3px 10px rgba(235, 133, 0,0.35)'
                    }}
                  >
                    🚀 Iniciar Importação
                  </button>
                </div>
              )}
            </div>
          )}

          {/* ── Barra de progresso ─────────────────────────────────────────────── */}
          {(processando || concluido) && (
            <div style={{ marginBottom: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ fontWeight: 700, color: '#333', fontSize: 14 }}>
                  {processando ? (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                      <span style={{
                        display: 'inline-block', width: 18, height: 18,
                        border: '3px solid rgb(235, 133, 0)', borderTop: '3px solid transparent',
                        borderRadius: '50%', animation: 'spin 0.8s linear infinite'
                      }} />
                      Processando produtos...
                    </span>
                  ) : '✅ Importação concluída!'}
                </span>
                <span style={{ fontSize: 13, color: '#666' }}>
                  {progresso.atual} de {progresso.total} — {pct}%
                </span>
              </div>
              <div style={{ background: '#e0e0e0', borderRadius: 20, height: 12, overflow: 'hidden' }}>
                <div style={{
                  height: '100%', borderRadius: 20,
                  background: concluido ? '#43a047' : 'linear-gradient(90deg, rgb(235, 133, 0), rgb(208, 116, 0))',
                  width: `${pct}%`, transition: 'width 0.3s'
                }} />
              </div>

              {concluido && (
                <div style={{ display: 'flex', gap: 16, marginTop: 12 }}>
                  <div style={{
                    flex: 1, background: '#e8f5e9', borderRadius: 8, padding: '10px 14px',
                    textAlign: 'center', borderLeft: '4px solid #43a047'
                  }}>
                    <div style={{ fontSize: 24, fontWeight: 700, color: '#2e7d32' }}>{sucessos}</div>
                    <div style={{ color: '#388e3c', fontSize: 12 }}>Cadastrados com sucesso</div>
                  </div>
                  <div style={{
                    flex: 1, background: '#ffebee', borderRadius: 8, padding: '10px 14px',
                    textAlign: 'center', borderLeft: '4px solid #e53935'
                  }}>
                    <div style={{ fontSize: 24, fontWeight: 700, color: '#c62828' }}>{erros}</div>
                    <div style={{ color: '#e53935', fontSize: 12 }}>Linhas com erro / ignoradas</div>
                  </div>
                </div>
              )}

              {processando && (
                <div style={{ textAlign: 'center', marginTop: 12 }}>
                  <button
                    onClick={cancelar}
                    style={{
                      background: '#fff', border: '1.5px solid #e53935', color: '#e53935',
                      borderRadius: 6, padding: '7px 20px', fontSize: 13, cursor: 'pointer'
                    }}
                  >
                    Cancelar
                  </button>
                </div>
              )}
            </div>
          )}

          {/* ── Log de resultados ──────────────────────────────────────────────── */}
          {resultados.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <p style={{ fontWeight: 700, color: '#333', fontSize: 14, marginBottom: 8 }}>
                📝 Log detalhado
              </p>
              <div style={{
                maxHeight: 240, overflowY: 'auto', border: '1px solid #e0e0e0',
                borderRadius: 8, fontSize: 12
              }}>
                {resultados.map((r, i) => (
                  <div
                    key={i}
                    style={{
                      padding: '7px 12px',
                      background: r.status === 'sucesso'
                        ? (i % 2 === 0 ? '#f9fff9' : '#f1fdf1')
                        : (i % 2 === 0 ? '#fff5f5' : '#ffefef'),
                      borderBottom: '1px solid #f0f0f0',
                      display: 'flex', alignItems: 'flex-start', gap: 8
                    }}
                  >
                    <span style={{ minWidth: 20 }}>{r.status === 'sucesso' ? '✅' : '❌'}</span>
                    <span style={{ minWidth: 70, color: '#888' }}>Linha {r.linha}</span>
                    <span style={{ minWidth: 200, fontWeight: 600, color: '#333' }}>{r.nome}</span>
                    <span style={{ color: r.status === 'sucesso' ? '#388e3c' : '#c62828' }}>{r.mensagem}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── Botões finais ──────────────────────────────────────────────────── */}
          {concluido && (
            <div style={{ textAlign: 'center', marginTop: 20, display: 'flex', justifyContent: 'center', gap: 14 }}>
              <button
                onClick={() => {
                  setArquivo(null);
                  setResultados([]);
                  setConcluido(false);
                  setProgresso({ atual: 0, total: 0 });
                  setErroArquivo('');
                  if (inputRef.current) inputRef.current.value = '';
                }}
                style={{
                  background: '#fff', border: '1.5px solid rgb(235, 133, 0)', color: 'rgb(235, 133, 0)',
                  borderRadius: 8, padding: '10px 24px', fontSize: 14,
                  fontWeight: 600, cursor: 'pointer'
                }}
              >
                Nova Importação
              </button>
              <button
                onClick={onFechar}
                style={{
                  background: 'linear-gradient(135deg, rgb(235, 133, 0), rgb(208, 116, 0))',
                  color: '#fff', border: 'none', borderRadius: 8,
                  padding: '10px 28px', fontSize: 14,
                  fontWeight: 700, cursor: 'pointer'
                }}
              >
                Fechar
              </button>
            </div>
          )}

        </div>
      </div>

      {/* Keyframes spinner */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
